// Why a geo is flagged, in words, with the numbers that produced the verdict.
//
// The verdict itself is one badge — "Offer", "Delivery" — which says what we
// concluded and nothing about how. Reading it required knowing the rules in
// lib/perf-report.ts by heart, so the page now spells out the symptom, what it
// was compared against, what was checked and ruled out, and what to go look at.
//
// Every line is conditional: a crash-free rate of 99.8% belongs in a delivery
// case (as a ruled-out explanation) and nowhere near a deposits case, and a
// deposits line in a case about registrations is noise.

import type { PerfRow, PerfThresholds, PerfVerdict } from '@/api/perf';

export interface PerfExplanation {
  /** One sentence: what is wrong and by how much. */
  symptom: string;
  /** The bar it was measured against, and where that bar comes from. */
  comparison: string[];
  /** Checked and dismissed — this is what makes the verdict trustworthy. */
  ruledOut: string[];
  /** Missing data the verdict had to work around. */
  caveat: string | null;
  /** What the gap is worth, when there is one. */
  price: string | null;
  /** Where to go looking. */
  checks: string[];
}

function pct(v: number | null, digits = 2): string {
  return v == null ? '—' : `${(v * 100).toFixed(digits)}%`;
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function num(v: number | null): string {
  return v == null ? '—' : v.toLocaleString('ru-RU');
}

/** Russian count forms: 1 клик, 2 клика, 5 кликов — 11..14 take the last one. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

function clicksWord(n: number | null): string {
  return n == null ? '—' : `${num(n)} ${plural(n, 'клик', 'клика', 'кликов')}`;
}

function opensWord(n: number | null): string {
  return n == null ? '—' : `${num(n)} ${plural(n, 'первое открытие', 'первых открытия', 'первых открытий')}`;
}

function regsWord(n: number | null): string {
  return n == null ? '—' : `${num(n)} ${plural(n, 'регистрация', 'регистрации', 'регистраций')}`;
}

/** Signed change of value against base, worded so a rise never reads as a drop. */
function change(value: number | null, base: number | null): string | null {
  if (value == null || base == null || base <= 0) return null;
  const delta = (value / base - 1) * 100;
  if (!Number.isFinite(delta)) return null;
  if (Math.abs(delta) < 1) return 'без изменений';
  return delta > 0 ? `+${delta.toFixed(0)}%` : `−${Math.abs(delta).toFixed(0)}%`;
}

const CHECKS: Record<PerfVerdict, string[]> = {
  crash: [
    'Crashlytics по этой версии приложения',
    'привязан ли крэш к устройствам или ОС этого гео',
  ],
  delivery: [
    'ссылку и цепочку редиректов кампании для этого гео',
    'доступность приложения в Play в этой стране (гейт, возрастные ограничения)',
    'не режет ли антимодерка эти устройства',
  ],
  offer: [
    'жива ли офферная ссылка для этого гео и на том ли языке',
    'вебвью и force-offer на живом устройстве',
    'не сменил ли партнёр лендинг',
  ],
  dep: [
    'приходят ли постбеки по депозитам от партнёра',
    'минимальный депозит и платёжный флоу в оффере',
    'не переоценили ли выплаты',
  ],
  geo_lag: [
    'подходит ли оффер этому гео',
    'связку креатив/язык',
  ],
  none: [],
};

function symptomFor(r: PerfRow, t: PerfThresholds): string {
  const regs = `${pct(r.regRate)} (${num(r.regs)} из ${num(r.clicks)} кликов)`;
  switch (r.verdict) {
    case 'crash':
      return `Приложение падает у этих пользователей: crash-free ${r.crashFreePct?.toFixed(1)}% при пороге ${t.crashFreePct}%. `
        + `Регистрации ${regs} — сначала крэш, остальные цифры читать после него.`;
    case 'delivery': {
      const arrivalLow = r.arrival != null && r.arrival * 100 < t.arrivalMinPct;
      if (arrivalLow) {
        return `Клики не превращаются в открытия приложения: ${opensWord(r.fbNewUsers)} на ${clicksWord(r.clicks)} `
          + `(${pct(r.arrival, 0)} при пороге ${t.arrivalMinPct}%). Пользователи не доходят — регистрации упали до ${pct(r.regRate)}.`;
      }
      const drop = change(r.fbNewUsers, r.baseFbNewUsers);
      return `Прибытия рухнули: ${num(r.fbNewUsers)} против ${num(r.baseFbNewUsers)} в прошлом периоде`
        + `${drop ? ` (${drop})` : ''} — трафик перестал доходить до приложения, регистрации следом упали до ${pct(r.regRate)}.`;
    }
    case 'offer':
      return `Люди доходят до приложения${r.arrival != null ? ` (${pct(r.arrival, 0)} от кликов)` : ''}, но перестали регистрироваться: `
        + `${regs} против достижимых ${pct(r.benchRegRate)}.`;
    case 'dep':
      return `Регистрации держатся (${pct(r.regRate)}), а депозиты рухнули: ${pct(r.depRate, 1)} от регистраций `
        + `против ${pct(r.baseDepRate, 1)} в прошлом периоде. Ломается после регистрации.`;
    case 'geo_lag': {
      const rose = change(r.regRate, r.baseRegRate);
      const grew = r.baseRegRate != null && r.regRate != null && r.regRate > r.baseRegRate;
      return (grew && rose ? `Конверсия даже выросла к прошлому периоду (${rose}), но гео структурно отстаёт: ` : 'Ничего не сломалось — гео структурно отстаёт: ')
        + `${pct(r.regRate)} против ${pct(r.geoMedianRegRate)} у других гео приложения.`;
    }
    case 'none':
      return `Ни один порог не перейдён — гео просто ниже достижимого барьера ${pct(r.benchRegRate)} `
        + `на ${regsWord(r.lostRegs)}.`;
  }
}

/**
 * Null when there is nothing to explain: no verdict and no measured loss. Rows
 * under the noise floor never reach here — they carry neither.
 */
export function explainRow(r: PerfRow, t: PerfThresholds): PerfExplanation | null {
  if (r.verdict === 'none' && r.lostRegs === 0) return null;

  const regChange = change(r.regRate, r.baseRegRate);
  const comparison: string[] = [];

  if (r.verdict === 'dep') {
    // The case is about deposits, so the registration bar is context here, not
    // the argument — leading with it sent the reader after the wrong number.
    const depChange = change(r.depRate, r.baseDepRate);
    comparison.push(`Депозиты: ${pct(r.depRate, 1)} от регистраций против ${pct(r.baseDepRate, 1)} в прошлом периоде${depChange ? ` (${depChange})` : ''}.`);
    if (r.baseRegRate != null) {
      const fell = r.regRate != null && r.regRate < r.baseRegRate;
      comparison.push(fell
        ? `Регистрации: ${pct(r.regRate)} против ${pct(r.baseRegRate)}${regChange ? ` (${regChange})` : ''} — падение не дотянуло до порога ${t.dropPct}%, поэтому вердикт про депозиты.`
        : `Регистрации не просели: ${pct(r.regRate)} против ${pct(r.baseRegRate)}${regChange ? ` (${regChange})` : ''}.`);
    }
  } else {
    const fromBaseline = r.baseRegRate != null && r.benchRegRate === r.baseRegRate;
    if (r.benchRegRate != null && fromBaseline) {
      // One line instead of two: here the bar IS the baseline.
      comparison.push(`Достижимый барьер ${pct(r.benchRegRate)} — столько это гео давало в прошлом периоде${regChange ? ` (${regChange})` : ''}.`);
    } else {
      if (r.benchRegRate != null) comparison.push(`Достижимый барьер ${pct(r.benchRegRate)} — медиана других гео приложения.`);
      if (r.baseRegRate != null) comparison.push(`Прошлый период, это же гео: ${pct(r.baseRegRate)}${regChange ? ` (${regChange})` : ''}.`);
    }
    if (r.geoMedianRegRate != null && r.benchRegRate !== r.geoMedianRegRate) {
      comparison.push(`Другие гео приложения сейчас: ${pct(r.geoMedianRegRate)} (медиана, взвешенная по кликам).`);
    }
  }

  // Only the explanations that were actually considered and dismissed. A
  // crash-free rate quoted in a deposits case explains nothing.
  const ruledOut: string[] = [];
  if (r.verdict !== 'crash' && r.crashFreePct != null && r.crashFreePct >= t.crashFreePct) {
    ruledOut.push(`крэшей нет (crash-free ${r.crashFreePct.toFixed(1)}%)`);
  }
  if (r.verdict !== 'delivery' && r.arrival != null && r.arrival * 100 >= t.arrivalMinPct) {
    ruledOut.push(`трафик доходит — ${opensWord(r.fbNewUsers)} на ${clicksWord(r.clicks)} (${pct(r.arrival, 0)})`);
  }
  // "Deposits did not fall" is only worth saying where there were deposits to
  // fall — at 0% against 0% it is a fact about nothing.
  if (r.verdict !== 'dep' && r.regs > 0 && r.depRate != null && r.baseDepRate != null
      && r.depRate >= r.baseDepRate && (r.deps > 0 || r.baseDepRate > 0)) {
    ruledOut.push(`депозиты не просели (${pct(r.depRate, 1)} от регистраций)`);
  }

  return {
    symptom: symptomFor(r, t),
    comparison,
    ruledOut,
    caveat: r.fbMissing
      ? 'Firebase по этому приложению не отвечает — вердикт построен только на данных трекера, прибытия не проверить.'
      : null,
    price: r.lostRegs > 0
      ? `${regsWord(r.lostRegs)} ≈ ${money(r.lostMoneyCents)} по цене регистрации этого приложения.`
      : null,
    checks: CHECKS[r.verdict],
  };
}
